import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@urbanx/agx.ui-components2';
import { type TaskArrivalEvent, useTaskArrivals } from 'hooks/useTaskArrivals';
import type {
  TaskArrivalNotification,
  TaskAssigneeChangedNotification,
} from 'types/pushNotifications';
import type { TaskFilterState } from './TaskFilters/TaskFiltersState';
import { createTaskSearchQueryKey } from './taskSearchQuery';

const taskCollectionChangeNotificationId = 'task-collection-change-notification';
const unassignedUserId = 'unassigned';

const matchesSelection = (selection: readonly unknown[] | undefined, value: unknown) =>
  !selection?.length || selection.includes(value);

const matchesAssigneeSelection = (
  assignedToUserIds: readonly string[] | undefined,
  assignedTo: string | undefined
) => {
  if (!assignedToUserIds?.length) return true;
  if (assignedTo) return assignedToUserIds.includes(assignedTo);

  return assignedToUserIds.some(
    (selectedAssignee) => selectedAssignee.toLowerCase() === unassignedUserId
  );
};

export const taskArrivalMatchesFilters = (
  task: TaskArrivalNotification,
  filters: TaskFilterState
) => {
  if (
    !task.taskId ||
    !task.campaignId ||
    !task.agencyId ||
    !task.stage ||
    !task.teamType ||
    !task.isStaffVisible ||
    !Array.isArray(task.taskDueFilters)
  ) {
    return false;
  }

  if (filters.addressSearchText?.trim()) return false;
  if (!matchesSelection(filters.states, task.state)) return false;
  if (!matchesSelection(filters.agencyIds, task.agencyId)) return false;
  if (!matchesSelection(filters.teamTypes, task.teamType)) return false;
  if (!matchesSelection(filters.stages, task.stage)) return false;
  if (filters.taskDue && !task.taskDueFilters.includes(filters.taskDue)) return false;

  if (!matchesAssigneeSelection(filters.assignedToUserIds, task.assignedTo)) return false;

  return true;
};

export const taskAssigneeChangeMatchesFilters = (
  task: TaskAssigneeChangedNotification,
  filters: TaskFilterState
) => {
  const matchedBefore = taskArrivalMatchesFilters(
    { ...task, assignedTo: task.previousAssignedTo },
    filters
  );
  const matchesNow = taskArrivalMatchesFilters(task, filters);

  return matchedBefore !== matchesNow;
};

interface TaskCollectionChangeNotifierOptions {
  filters?: TaskFilterState;
  baselineReady: boolean;
  refreshTasks: () => Promise<unknown>;
}

export const useTaskCollectionChangeNotifier = ({
  filters,
  baselineReady,
  refreshTasks,
}: TaskCollectionChangeNotifierOptions) => {
  const knownTaskIds = useRef(new Set<string>());
  const filterKey = JSON.stringify(filters);
  const { pushToast, removeToast } = useToast();
  const queryClient = useQueryClient();

  const clearNotification = useCallback(() => {
    knownTaskIds.current.clear();
    removeToast(taskCollectionChangeNotificationId);
  }, [removeToast]);

  const refreshTaskList = useCallback(async () => {
    await refreshTasks();
    clearNotification();
  }, [clearNotification, refreshTasks]);

  const showTaskArrival = useCallback(
    (event: TaskArrivalEvent) => {
      if (!baselineReady || !filters) return;

      if (filters.leadAgentIds?.length) {
        void queryClient.invalidateQueries({
          queryKey: createTaskSearchQueryKey(filters),
          exact: true,
        });
        return;
      }

      const matchesFilters =
        event.kind === 'assignee-changed'
          ? taskAssigneeChangeMatchesFilters(event.task, filters)
          : taskArrivalMatchesFilters(event.task, filters);
      if (!matchesFilters) return;

      knownTaskIds.current.add(event.task.taskId);
      const numberOfKnownChanges = knownTaskIds.current.size;

      pushToast(
        {
          message: `There ${numberOfKnownChanges === 1 ? 'is' : 'are'} ${numberOfKnownChanges} task list update${numberOfKnownChanges === 1 ? '' : 's'} available.`,
          action: {
            label: 'Refresh',
            onClick: refreshTaskList,
          },
        },
        taskCollectionChangeNotificationId
      );
    },
    [baselineReady, filters, pushToast, queryClient, refreshTaskList]
  );

  useTaskArrivals(showTaskArrival);

  useEffect(() => clearNotification(), [clearNotification, filterKey]);
  useEffect(() => () => removeToast(taskCollectionChangeNotificationId), [removeToast]);
};
